import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search,
  Work,
  LocationOn,
  AttachMoney,
  People,
  ContentCopy,
  Check,
  ArrowForward
} from '@mui/icons-material';
import {
  getPublicJobs,
  PublicJob,
  JobFilters,
  locationOptions,
  employmentTypeOptions
} from '@/services/PublicJobService';

const PublicJobsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<JobFilters>({
    search: '',
    location: '',
    employment_type: ''
  });
  const [copiedJobId, setCopiedJobId] = useState<number | null>(null);

  const fetchJobs = async () => {
    setLoading(true);
    const response = await getPublicJobs(filters);
    if (response.success) {
      setJobs(response.jobs);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchJobs();
  }, [filters.location, filters.employment_type]);

  const handleSearch = () => {
    fetchJobs();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      fetchJobs();
    }
  };

  const handleCopyLink = async (e: React.MouseEvent, jobId: number) => {
    e.stopPropagation();
    const publicUrl = `${window.location.origin}/careers/${jobId}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedJobId(jobId);
      setTimeout(() => setCopiedJobId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return null;
    const format = (n: number) => n.toLocaleString('en-IN');
    if (min && max) return `₹${format(min)} - ₹${format(max)}`;
    if (min) return `From ₹${format(min)}`;
    return `Up to ₹${format(max!)}`;
  };

  // Nucleus brand colors
  const brandColors = {
    primary: '#262b45',
    accent: '#4f46e5',
    gradient: isDark
      ? 'linear-gradient(135deg, #1a1a2e 0%, #262b45 100%)'
      : 'linear-gradient(135deg, #262b45 0%, #4f46e5 100%)',
    buttonGradient: 'linear-gradient(135deg, #262b45 0%, #4f46e5 100%)',
    buttonHover: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Header */}
      <Box
        sx={{
          background: brandColors.gradient,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />

        {/* Nav */}
        <Box sx={{ py: 2, px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/assets/logos/logo-192.png"
              alt="Nucleus"
              sx={{ height: 36, width: 36 }}
            />
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
              Multiplierskraft Careers
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            onClick={() => navigate('/login')}
          >
            Employee Login
          </Button>
        </Box>

        {/* Hero Content */}
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center', position: 'relative' }}>
          <Typography
            variant="h2"
            fontWeight="800"
            sx={{
              fontSize: { xs: '2rem', md: '3.5rem' },
              mb: 2,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Join Our Team
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              mb: 5,
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Discover exciting opportunities and build your career with an innovative team that values growth and creativity
          </Typography>

          {/* Search Box */}
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              overflow: 'visible',
              bgcolor: theme.palette.background.paper
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Job title, keyword..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyPress={handleKeyPress}
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={filters.location}
                    label="Location"
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc.value} value={loc.value}>{loc.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Job Type</InputLabel>
                  <Select
                    value={filters.employment_type}
                    label="Job Type"
                    onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {employmentTypeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    background: brandColors.buttonGradient,
                    boxShadow: `0 4px 12px ${alpha(brandColors.primary, 0.4)}`,
                    '&:hover': {
                      background: brandColors.buttonHover
                    }
                  }}
                >
                  Search Jobs
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Jobs List */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={48} sx={{ color: brandColors.accent }} />
          </Box>
        ) : jobs.length === 0 ? (
          <Card
            sx={{
              textAlign: 'center',
              py: 10,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              bgcolor: theme.palette.background.paper
            }}
          >
            <Work sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No open positions at the moment
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Check back soon for new opportunities!
            </Typography>
          </Card>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h5" fontWeight="600" color="text.primary">
                {jobs.length} Open Position{jobs.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {jobs.map((job) => (
                <Card
                  key={job.id}
                  sx={{
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    bgcolor: theme.palette.background.paper,
                    '&:hover': {
                      boxShadow: `0 8px 30px ${alpha(brandColors.accent, 0.15)}`,
                      transform: 'translateY(-4px)',
                      borderColor: brandColors.accent
                    }
                  }}
                  onClick={() => navigate(`/careers/${job.id}`)}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Typography
                          variant="h5"
                          fontWeight="700"
                          sx={{
                            color: isDark ? theme.palette.primary.light : brandColors.primary,
                            mb: 1,
                            '&:hover': { color: brandColors.accent }
                          }}
                        >
                          {job.title}
                        </Typography>

                        {job.department && (
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {job.department}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                          {job.location && (
                            <Chip
                              icon={<LocationOn sx={{ fontSize: 16 }} />}
                              label={job.location}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 500,
                                '& .MuiChip-icon': { color: theme.palette.info.main }
                              }}
                            />
                          )}
                          {job.employment_type && (
                            <Chip
                              label={job.employment_type.replace('-', ' ')}
                              size="small"
                              sx={{
                                bgcolor: alpha(brandColors.accent, 0.1),
                                color: brandColors.accent,
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                          )}
                          <Chip
                            icon={<People sx={{ fontSize: 16 }} />}
                            label={`${job.positions_count} opening${job.positions_count > 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontWeight: 500,
                              '& .MuiChip-icon': { color: theme.palette.success.main }
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.6
                          }}
                        >
                          {job.description}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        {formatSalary(job.salary_min, job.salary_max) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney sx={{ fontSize: 20, color: theme.palette.success.main }} />
                            <Typography variant="body1" fontWeight="600" sx={{ color: theme.palette.success.main }}>
                              {formatSalary(job.salary_min, job.salary_max)}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={copiedJobId === job.id ? "Copied!" : "Copy link"}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyLink(e, job.id)}
                              sx={{
                                bgcolor: copiedJobId === job.id
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : theme.palette.action.hover,
                                color: copiedJobId === job.id
                                  ? theme.palette.success.main
                                  : theme.palette.text.secondary,
                                '&:hover': { bgcolor: theme.palette.action.selected }
                              }}
                            >
                              {copiedJobId === job.id ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/careers/${job.id}`);
                            }}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              background: brandColors.buttonGradient,
                              '&:hover': {
                                background: brandColors.buttonHover
                              }
                            }}
                          >
                            View & Apply
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)'
            : 'linear-gradient(135deg, #262b45 0%, #1a1a2e 100%)',
          color: 'white',
          py: 6,
          mt: 6
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/assets/logos/logo-192.png"
                alt="Nucleus"
                sx={{ height: 32, width: 32, opacity: 0.9 }}
              />
              <Typography variant="h6" fontWeight="600" sx={{ color: 'white' }}>
                Nucleus
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
              Join us and be part of something great
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Powered by Nucleus HR
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicJobsPage;
